import { APIProvider } from '@vis.gl/react-google-maps';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { useStorage } from '../../hooks/use-storage';
import { useSwal } from '../../hooks/use-swal';
import { AddressStreetNumberModalBody } from '../../components/location/address-street-number-modal-body';
import { useErrorHandler } from '../../hooks/use-error-handler';
import {
  parseMapRoute,
  parseMapStreetNumber,
} from '../../utils/manager/map-manager';
import { useMapAPI } from '../../apis/map-api';
import { useAnalytics } from '../../utils/setup/analytics-setup';

function useAddressStreetNumberModal() {
  const storage = useStorage();
  const { config, menuInfo } = storage;
  const { swal } = useSwal({ storage });
  const { handleErrorOnModal } = useErrorHandler();
  const { saveMapBaseLocationFromRemote } = useMapAPI();
  const { analyticsPageView } = useAnalytics();
  const { t } = useTranslation();

  const streetNumberRef = useRef();
  const noStreetNumberRef = useRef();

  const placeDetailsRef = useRef();
  const baseLocationRef = useRef();

  const didOpen = () => {
    analyticsPageView('Address Street Number');
  };

  async function clear() {
    streetNumberRef.current = null;
    noStreetNumberRef.current = false;
    placeDetailsRef.current = null;
    baseLocationRef.current = null;
  }

  async function show({ placeDetails, baseLocation }) {
    let geocoder;

    const result = await swal.fire({
      html: (
        <APIProvider
          apiKey={config?.maps?.browser?.api_key}
          language={menuInfo?.i18n?.default_language?.tag}
        >
          <AddressStreetNumberModalBody
            config={config}
            placeDetails={placeDetails}
            baseLocation={baseLocation}
            onGeocoderLoaded={(pGeocoder) => {
              geocoder = pGeocoder;
            }}
            streetNumber={streetNumberRef.current}
            onStreetNumberChange={(value) => {
              swal.resetValidationMessage();
              streetNumberRef.current = value;
            }}
            noStreetNumber={noStreetNumberRef.current}
            onNoStreetNumberChange={(value) => {
              swal.resetValidationMessage();
              noStreetNumberRef.current = value;
            }}
          />
        </APIProvider>
      ),
      didOpen,
      preConfirm: () => preConfirm({ geocoder, placeDetails, baseLocation }),
      showConfirmButton: true,
      showDenyButton: false,
      showCancelButton: true,
      showLoaderOnConfirm: true,
      confirmButtonText: t('next'),
      cancelButtonText: t('back'),
    });

    if (result.isConfirmed) {
      result.placeDetails = placeDetailsRef.current;
      result.baseLocation = baseLocationRef.current;
      result.noStreetNumber = noStreetNumberRef.current;
    }

    if (result.isDismissed) {
      result.wasStreetNumberBackButtonClicked = true;
      result.wasImcompleteBackButtonClicked = true;
    }

    return result;
  }

  async function preConfirm({ geocoder, placeDetails, baseLocation }) {
    try {
      if (_.isEmpty(streetNumberRef.current) && !noStreetNumberRef.current) {
        swal.showValidationMessage(t('address-street-number-modal.empty'));
        return false;
      }

      const streetNumber = noStreetNumberRef.current
        ? t('location.no-street-number-term')
        : streetNumberRef.current;

      const address = `${parseMapRoute(placeDetails)}, ${streetNumber}, ${baseLocation.neighborhood?.name ?? ''}, ${baseLocation.city?.name ?? ''}, ${baseLocation.state?.name ?? ''}, ${baseLocation.country?.name ?? ''}`;
      const result = await geocoder?.geocode({ address });
      const rPlaceDetails = _.head(result?.results);

      if (
        _.isEmpty(rPlaceDetails) ||
        _.isEmpty(parseMapRoute(rPlaceDetails)) ||
        (!_.isEmpty(streetNumberRef.current) &&
          _.isEmpty(parseMapStreetNumber(rPlaceDetails)))
      ) {
        swal.showValidationMessage(
          t('address-street-number-modal.invalid-address')
        );
        return false;
      }

      const response = await saveMapBaseLocationFromRemote(rPlaceDetails);

      placeDetailsRef.current = rPlaceDetails;
      baseLocationRef.current = response.data;
    } catch (error) {
      handleErrorOnModal(error);
      swal.hideLoading();
      return false;
    }

    return true;
  }

  return {
    clearAddressStreetNumberModal: clear,
    showAddressStreetNumberModal: show,
  };
}

export { useAddressStreetNumberModal };
